import React, { useEffect, useMemo, useState } from 'react';
import { FileVideo, RefreshCw } from 'lucide-react';
import { apiService, RealtimeFeed } from '../../services/api';
import { useTabActivity } from '../../contexts/TabActivityContext';
import { getRealtimeErrorMessage } from '../../features/realtime/errors';
import { SegmentedPlayer } from '../SegmentedPlayer';
import { SegmentedTimeline } from '../SegmentedTimeline';
import './RealtimeRecordingPlayer.css';

const elapsed = (feed: RealtimeFeed, duration: number) => feed.live_at
  ? Math.max(0, Math.min(duration, ((feed.status === 'live' ? Date.now() : Date.parse(feed.stopped_at || feed.live_at)) - Date.parse(feed.live_at)) / 1000)) : 0;

export default function RealtimeRecordingPlayer({ feed }: { feed: RealtimeFeed }) {
  const { isActive } = useTabActivity();
  const duration = Math.min(600, feed.test_duration_seconds || 600);
  const [time, setTime] = useState(() => elapsed(feed, duration));
  const [playing, setPlaying] = useState(feed.status === 'live');
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const active = feed.status === 'live';
  // The capture clock keeps running across tabs. Rejoin it when this preview
  // becomes visible; preview controls never alter the extraction session.
  useEffect(() => {
    if (isActive && active) { setTime(elapsed(feed, duration)); setPlaying(true); }
    else if (!active) setPlaying(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed.runtime_id, feed.live_at, active, isActive, duration]);
  useEffect(() => {
    const controller = new AbortController();
    setUrl(''); setError('');
    if (feed.test_video_id) void apiService.getRealtimeRecordingPlayback(feed.project_id, feed.test_video_id, controller.signal)
      .then(result => { if (!controller.signal.aborted) setUrl(result.playback_url); })
      .catch(failure => { if (!controller.signal.aborted) setError(getRealtimeErrorMessage(failure, 'Unable to load the recording. Try again.')); });
    return () => controller.abort();
  }, [feed.project_id, feed.test_video_id, revision]);
  const segments = useMemo(() => url ? [{ id: `${feed.runtime_id}:${feed.test_video_id}`, playbackUrl: url, previewGifUrl: '', duration, start: 0, end: duration }] : [], [feed.runtime_id, feed.test_video_id, duration, url]);
  const updateTime = (value: number) => {
    const bounded = Math.max(0, Math.min(duration, value)); setTime(bounded);
    if (value >= duration) setPlaying(false);
  };
  return <section className="realtime-recording-player" aria-label={`Test video ${feed.name}`}>
    <header><span className="realtime-feed-status"><FileVideo size={13} /> Video test</span><strong>{feed.test_video_name || 'Saved recording'}</strong></header>
    {error ? <div className="realtime-player-placeholder" role="alert"><span>{error}</span><button type="button" className="action-button" onClick={() => setRevision(value => value + 1)}>Retry preview</button></div>
      : segments.length ? <><SegmentedPlayer segments={segments} globalTime={time} onGlobalTimeChange={updateTime} playing={playing && isActive && time < duration} muted onPlayingChange={setPlaying} onError={() => setError('This recording could not be played. Try an MP4 with H.264 video for browser compatibility.')} /><SegmentedTimeline segments={segments} globalTime={time} totalDuration={duration} onChange={updateTime} /></>
        : <div className="realtime-player-placeholder" role="status" aria-label="Loading test video"><RefreshCw size={24} className="spinning" /><span>Loading recording…</span></div>}
    <p className="realtime-help">{active ? 'Testing' : feed.status === 'stopping' ? 'Finishing test' : feed.status === 'starting' || feed.status === 'connecting' ? 'Preparing test' : 'Test stopped'} · First 10 minutes maximum. Preview controls do not pause processing.</p>
  </section>;
}
